import { useEffect, useState } from 'react';

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
};

const KEY = 'lobu_install_dismissed_at';
const DAYS = 14;

function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  if (window.matchMedia?.('(display-mode: standalone)').matches) return true;
  // iOS Safari
  // @ts-expect-error vendor flag
  return !!window.navigator.standalone;
}
function isIOS(): boolean {
  if (typeof navigator === 'undefined') return false;
  // @ts-expect-error MSStream check
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}
function shouldShow(): boolean {
  if (isStandalone()) return false;
  const v = parseInt(localStorage.getItem(KEY) || '0', 10);
  if (v && Date.now() - v < DAYS * 86400000) return false;
  return true;
}

export default function InstallButton() {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [visible, setVisible] = useState(false);
  const [iosOpen, setIosOpen] = useState(false);

  useEffect(() => {
    function onBeforeInstallPrompt(e: Event) {
      e.preventDefault();
      setDeferred(e as BeforeInstallPromptEvent);
      if (shouldShow()) setTimeout(() => setVisible(true), 1500);
    }
    function onInstalled() {
      setVisible(false);
      setDeferred(null);
      localStorage.removeItem(KEY);
    }
    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt as EventListener);
    window.addEventListener('appinstalled', onInstalled);
    // iOS fallback
    const t = setTimeout(() => {
      if (!deferred && isIOS() && shouldShow()) setVisible(true);
    }, 4000);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt as EventListener);
      window.removeEventListener('appinstalled', onInstalled);
      clearTimeout(t);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!visible) return null;

  async function install() {
    if (deferred) {
      try {
        await deferred.prompt();
        await deferred.userChoice;
      } catch { /* ignore */ }
      finally {
        setVisible(false);
        setDeferred(null);
        localStorage.setItem(KEY, String(Date.now()));
      }
    } else if (isIOS()) {
      setIosOpen(true);
      setVisible(false);
    } else {
      setVisible(false);
    }
  }
  function dismiss() {
    setVisible(false);
    localStorage.setItem(KEY, String(Date.now()));
  }

  const banner = (
    <div style={{
      position:'fixed', bottom:16, left:16, right:16, maxWidth:520, margin:'0 auto',
      background:'#0D47A1', color:'#fff', borderRadius:14, padding:'14px 18px',
      display:'flex', alignItems:'center', gap:12,
      boxShadow:'0 12px 40px rgba(0,0,0,.35)', zIndex:99998,
      fontFamily:'system-ui,-apple-system,sans-serif',
    }}>
      <div style={{fontSize:28, lineHeight:1}}>📱</div>
      <div style={{flex:1, minWidth:0}}>
        <div style={{fontWeight:700, fontSize:14, marginBottom:2}}>Instala la app</div>
        <div style={{fontSize:12, opacity:.85, lineHeight:1.3}}>Acceso directo desde la pantalla de inicio. Funciona sin conexión.</div>
      </div>
      <button type="button" onClick={install} style={{background:'#fff',color:'#0D47A1',border:'none',padding:'9px 16px',borderRadius:8,fontWeight:700,cursor:'pointer',fontSize:13,whiteSpace:'nowrap'}}>Instalar</button>
      <button type="button" title="Ocultar 14 días" onClick={dismiss} style={{background:'transparent',color:'#fff',border:'1px solid rgba(255,255,255,.4)',padding:'8px 11px',borderRadius:8,cursor:'pointer',fontSize:13}}>✕</button>
    </div>
  );

  if (iosOpen) {
    return (
      <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.65)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:99999,padding:20}}>
        <div style={{background:'#fff',maxWidth:380,width:'100%',borderRadius:16,padding:'26px 22px',textAlign:'center',fontFamily:'system-ui,-apple-system,sans-serif',color:'#0F172A'}}>
          <div style={{fontSize:48,lineHeight:1}}>📱</div>
          <h3 style={{margin:'10px 0 8px',fontSize:18,fontWeight:700}}>Instalar en iPhone / iPad</h3>
          <p style={{fontSize:14,color:'#475569',lineHeight:1.55,margin:'0 0 6px'}}>1. Pulsa <b>Compartir</b> ⎘ en la barra inferior de Safari.</p>
          <p style={{fontSize:14,color:'#475569',lineHeight:1.55,margin:'0 0 6px'}}>2. Desplázate y pulsa <b>Añadir a pantalla de inicio</b> ➕.</p>
          <p style={{fontSize:14,color:'#475569',lineHeight:1.55,margin:'0 0 16px'}}>3. Confirma <b>Añadir</b>.</p>
          <button type="button" onClick={() => { setIosOpen(false); localStorage.setItem(KEY, String(Date.now())); }} style={{background:'#0D47A1',color:'#fff',border:'none',padding:'11px 26px',borderRadius:9,fontWeight:700,cursor:'pointer',fontSize:14}}>Entendido</button>
        </div>
      </div>
    );
  }

  return banner;
}
